import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { zodSavedFilterResponseSchema } from '../validations';

import {
  type UpdateSavedFilterPayload,
  type UpdateSavedFilterResponse,
} from './types';

/**
 * Updates a saved filter using optimistic concurrency.
 *
 * The caller-provided expectedVersion payload field is converted to
 * expected_version by the ky case converter before the request is sent.
 *
 * @param savedFilterId - The saved-filter ID to update.
 * @param payload - Saved-filter patch fields, including expectedVersion.
 * @param options - Optional ky request options (e.g., abort signal).
 * @returns The updated saved filter when the API returns a body.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/update_saved_filter_user_saved_filters__saved_filter_id__patch
 */
export const updateSavedFilter = async (
  savedFilterId: UUID,
  payload: UpdateSavedFilterPayload,
  options?: KyRequestOptions,
): Promise<UpdateSavedFilterResponse> => {
  const response = await vestigasApi.patch(
    ENDPOINT.USER.SAVED_FILTER(savedFilterId),
    {
      ...options,
      json: payload,
    },
  );

  if (response.status === 204) return undefined;

  const json =
    await response.json<Exclude<UpdateSavedFilterResponse, undefined>>();
  const validated = zodSavedFilterResponseSchema.safeParse(json);

  if (!validated.success) {
    console.warn(
      '[updateSavedFilter] Response validation failed',
      validated.error,
    );
    return json;
  }

  return validated.data;
};
